import { FileDownload } from "@mui/icons-material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { formatWeekRangeWithMonth } from "../../../../utils/dateFormatting";
import type { RecurringGapsExportLabels, RecurringGapsExportView } from "../export/createRecurringGapsWorkbook";
import { useRecurringGapsExport } from "../hooks/useRecurringGapsExport";
import { displayedDateSpan, formatWeekHeader } from "../utils/gridDates";
import { gapsRowLabelKey } from "../utils/metricRows";
import { DAY_KEYS } from "../utils/weekStart";

const DOWNLOADED_AT_FORMAT = "d MMM yyyy, h:mm a";
const FILE_DATE_FORMAT = "yyyy-MM-dd";

type Props = {
	/** What the grid is drawing right now. */
	view: RecurringGapsExportView;
	timezone: string;
	jobId: string;
	jobDisplayName: string;
	detailedView: boolean;
};

const toSlug = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		// A name with no Latin letters (Hebrew, say) collapses to underscores, which would only pad the file name.
		.replace(/^_+|_+$/g, "");

/** The small download icon in the Recurring Gaps header: the grid, as it is on screen, as Excel. */
export const RecurringGapsExportButton: FC<Props> = ({ view, timezone, jobId, jobDisplayName, detailedView }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { dates, weekNumbers } = view;

	const getExport = useCallback((): { view: RecurringGapsExportView; labels: RecurringGapsExportLabels } => {
		const labels: RecurringGapsExportLabels = {
			sheetName: t("hiring.gaps.title"),
			// The catalog doesn't always carry a name for an eligible job, and the page leaves that gap visible
			// rather than invent one — so the title is then just the table's name, with no dangling separator.
			title: jobDisplayName ? t("hiring.gaps.export.title", { job: jobDisplayName }) : t("hiring.gaps.title"),
			subtitle: t("hiring.gaps.export.subtitle", {
				department: chosenDepartment.display_name,
				timestamp: DateTime.now().setZone(timezone).toFormat(DOWNLOADED_AT_FORMAT),
			}),
			legend: t("hiring.gaps.legend", { range: formatWeekRangeWithMonth(...displayedDateSpan(dates)) }),
			shiftHeader: t("hiring.gaps.shift"),
			rowHeader: t("hiring.gaps.row"),
			templateHppd: t("hiring.gaps.templateHppd"),
			weekHeader: (weekIdx) => formatWeekHeader(dates, weekIdx, weekNumbers[weekIdx], timezone, t),
			dayHeader: (dayIdx) => t(`hiring.day.${DAY_KEYS[dayIdx]}`),
			rowLabel: (rowType) => t(gapsRowLabelKey(rowType)),
		};
		return { view, labels };
	}, [t, view, dates, weekNumbers, timezone, jobDisplayName, chosenDepartment.display_name]);

	const fileName = useMemo(() => {
		const [firstDate] = displayedDateSpan(dates);
		// DEFAULT: no job segment when the job has no catalog name or one with no Latin letters, and no date
		// segment when the payload has no dates — the file still downloads. Never the job id: an internal id
		// is not a name anyone recognises.
		const parts = ["recurring_gaps", toSlug(jobDisplayName), firstDate?.toFormat(FILE_DATE_FORMAT) ?? ""];
		return `${parts.filter((part) => part.length > 0).join("_")}.xlsx`;
	}, [dates, jobDisplayName]);

	const analyticsProps = useMemo(
		() => ({
			department_id: chosenDepartment.department_id,
			job_id: jobId,
			detailed_view: detailedView,
			visible_shift_count: view.shiftLabels.length,
			rows_per_shift: view.rowTypes.length,
			week_count: view.gridWeeks,
		}),
		[chosenDepartment.department_id, jobId, detailedView, view.shiftLabels.length, view.rowTypes.length, view.gridWeeks],
	);

	const { download, isDownloading } = useRecurringGapsExport({ getExport, fileName, analyticsProps });

	return (
		// `aria-label` as well as `tooltip`: SystemButton puts its tooltip on a wrapper Box, which would
		// leave an icon-only button with no accessible name.
		<SystemButton
			data-testid="hiring-gaps-export-button"
			data-annie-target="hiring-gaps-export"
			buttonVariant="borderless"
			size="small"
			iconButton
			loading={isDownloading}
			disabled={isDownloading}
			onClick={() => void download()}
			leadingIcon={<FileDownload />}
			tooltip={t("hiring.gaps.export.button")}
			aria-label={t("hiring.gaps.export.button")}
		/>
	);
};
